import type { ApiCtaButton, ApiCtaConfig } from '../../../internalTypes';
import type { SpecialPageCtas, SurveyCtaButton, SurveyPageCtaConfig } from '../../../types/survey';

export type MapCtaButtonOptions = {
  defaultLabel?: string;
  defaultEnabled?: boolean;
};

export function mapCtaButton(
  api: ApiCtaButton | undefined,
  language: string | undefined,
  options: MapCtaButtonOptions = {}
): SurveyCtaButton | undefined {
  if (!api) return undefined;

  const translation = language ? api.translations?.[language] : undefined;

  const label =
    translation?.buttonText?.trim() ||
    api.buttonText?.trim() ||
    options.defaultLabel?.trim() ||
    '';

  if (!label) return undefined;

  const enabled = translation?.enabled ?? api.enabled ?? options.defaultEnabled ?? true;

  const inputPlaceholder =
    translation?.inputPlaceholder?.trim() || api.inputPlaceholder?.trim();

  const ctaDescription =
    translation?.ctaDescription?.trim() || api.ctaDescription?.trim();

  return {
    label,
    enabled,
    ...(api.url?.trim() ? { url: api.url.trim() } : {}),
    ...(inputPlaceholder ? { inputPlaceholder } : {}),
    ...(ctaDescription ? { ctaDescription } : {}),
  };
}

export function mapPageCtaConfig(
  api: ApiCtaConfig | undefined,
  language: string
): SurveyPageCtaConfig | undefined {
  if (!api) return undefined;

  const next = mapCtaButton(api.next, language, { defaultLabel: 'Next' });
  const previous = mapCtaButton(api.previous, language, { defaultLabel: 'Back' });

  if (!next && !previous) return undefined;

  return {
    ...(next ? { next } : {}),
    ...(previous ? { previous } : {}),
  };
}

const SPECIAL_PAGE_CTA_KEYS = [
  'start',
  'redirect',
  'sendEmailResponse',
  'resumptionNotification',
  'reopenNotification',
] as const satisfies ReadonlyArray<keyof SpecialPageCtas>;

export function mapSpecialPageCtas(
  api: ApiCtaConfig | undefined,
  language: string
): SpecialPageCtas | undefined {
  if (!api) return undefined;

  const ctas: SpecialPageCtas = {};

  const start =
    mapCtaButton(api.others?.start, language) ??
    mapCtaButton(api.start, language);
  if (start) ctas.start = start;

  for (const key of SPECIAL_PAGE_CTA_KEYS) {
    if (key === 'start') continue;
    const mapped = mapCtaButton(api.others?.[key], language);
    if (mapped) ctas[key] = mapped;
  }

  return Object.keys(ctas).length > 0 ? ctas : undefined;
}
